import { Injectable } from '@angular/core';
import { CartActions, CartApiService, CustomActiveCartService } from '@cart/data-access';
import { CompanySelectors, StateWithCompany } from '@company/data-access';
import { EnvironmentService } from '@core/feature';
import { Actions, EffectNotification, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { UserIdService } from '@spartacus/core';
import { CustomUserActions } from '@user/data-access';
import { EMPTY, Observable, combineLatest, iif, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';

@Injectable()
export class CartOrganizationEffects {
  setOrganizationToCart$: Observable<CartActions.SetOrganizationToCart> = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomUserActions.SetLegalEntityStatus>(CustomUserActions.SET_LEGAL_ENTITY_STATUS),
      map((action) => action.payload),
      distinctUntilChanged(),
      switchMap((isLegal) =>
        this.store.select(CompanySelectors.getSelectedCompany).pipe(
          distinctUntilChanged(),
          map((selectedCompanyUid) => ({ isLegal, selectedCompanyUid })),
        ),
      ),
      map(({ isLegal, selectedCompanyUid }) => {
        if (isLegal && selectedCompanyUid) {
          return new CartActions.SetOrganizationToCart(selectedCompanyUid);
        }

        return new CartActions.SetOrganizationToCart(null);
      }),
    ),
  );

  putOrganizationToCart$: Observable<CartActions.SetOrganizationToCartSuccess | CartActions.SetOrganizationToCartFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType<CartActions.SetOrganizationToCart>(CartActions.SET_ORGANIZATION_TO_CART),
        map((action) => action.payload),
        distinctUntilChanged(),
        switchMap((organizationUid) => {
          return combineLatest({
            cartId: this.activeCartService.getActiveCartId().pipe(filter(Boolean)),
          }).pipe(
            take(1),
            map(({ cartId }) => ({ organizationUid, cartId })),
          );
        }),
        switchMap(({ organizationUid, cartId }) => {
          return this.occCartService.setOrganization(cartId, organizationUid).pipe(
            map(() => new CartActions.SetOrganizationToCartSuccess()),
            catchError((error: unknown) => {
              return of(new CartActions.SetOrganizationToCartFail(error));
            }),
          );
        }),
      ),
    );

  reloadCart$: Observable<CartActions.LoadCart> = createEffect(() =>
    this.actions$.pipe(
      ofType<CartActions.SetOrganizationToCartSuccess>(CartActions.SET_ORGANIZATION_TO_CART_SUCCESS),
      concatLatestFrom(() => [this.userIdService.getUserId(), this.activeCartService.getActiveCartId()]),
      map(([, userId, cartId]) => ({ userId, cartId })),
      map(({ userId, cartId }) => {
        return new CartActions.LoadCart({
          userId,
          cartId,
        });
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store<StateWithCompany>,
    private occCartService: CartApiService,
    private userIdService: UserIdService,
    private activeCartService: CustomActiveCartService,
    private environmentService: EnvironmentService,
  ) {}

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return iif(() => this.environmentService.isPlatformBrowser, resolvedEffects$, EMPTY);
  }
}
